// Ribbon commands (manifest ExecuteFunction actions) of the EAV tab: Refresh
// selection / sheet / workbook, as LSEG Workspace's PLRefresh commands,
// Search, Account and Settings, which open the task pane on their section,
// Insert Chart (insertChart.ts), and Save as Values / Restore All Functions
// (saveAsValues.ts, also on the cell menu). At startup, the #NAME? repair
// (repairNames.ts).
// The manifest's FunctionFile is the shared runtime's page (taskpane.html), and
// webpack bundles this file INTO functions.js (one bundle, one copy of
// functions.ts): a refresh must mark the very calls the custom functions keep.
import { isEavFormula, refresh, RefreshScope } from "../functions/functions";
import { excelFormulaPort, FormulaScope } from "../lib/excelPort";
import type { RefreshFn } from "../taskpane/refreshPane";
import { NoticeKind, PaneSection, requestPaneSection } from "../taskpane/paneSections";
import { excelChartPort, insertChart } from "./insertChart";
import { excelNameRepairPort, repairNameErrors, scheduleNameRepair } from "./repairNames";
import { createValuesPort, restoreFunctions, saveAsValues } from "./saveAsValues";

/** A ribbon action: refresh `scope`, then tell Office the command is done (always, or the ribbon stays busy). */
function refreshCommand(scope: RefreshScope) {
  return (event: Office.AddinCommands.Event): void => {
    refresh(scope)
      .catch((e) => console.error("EAV: refresh failed", e))
      .then(() => event.completed());
  };
}

/** A ribbon action: open the task pane, show `section` in it, then complete the event (always). */
function paneCommand(section: PaneSection) {
  return (event: Office.AddinCommands.Event): void => {
    Office.addin
      .showAsTaskpane()
      .then(() => requestPaneSection(section))
      .catch((e) => console.error("EAV: could not open the panel", e))
      .then(() => event.completed());
  };
}

/** Say `text` in the task pane (Insert Chart's and Save as Values' notices). */
function notifyInPane(text: string, kind: NoticeKind = "error"): void {
  Office.addin
    .showAsTaskpane()
    .then(() => requestPaneSection("notice", text, kind))
    .catch((e) => console.error("EAV: could not open the panel", e));
}

/**
 * Insert Chart: the event completes at once, since the chart waits for its
 * data (up to 90 s) and the ribbon should not stay busy; the shared runtime
 * carries on. What stops it is said in the panel.
 */
function insertChartCommand(event: Office.AddinCommands.Event): void {
  event.completed();
  insertChart(excelChartPort, notifyInPane).catch((e) => {
    console.error("EAV: Insert Chart failed", e);
    notifyInPane(`Insert Chart: ${e instanceof Error ? e.message : String(e)}`);
  });
}

const valuesPort = createValuesPort((scope) => excelFormulaPort.formulasIn(scope));

/**
 * Save as Values / Restore All Functions: the event completes once the cells
 * are written. A full success says nothing (the cells show it); anything left
 * undone, or a failure, is said in the panel.
 */
function valuesCommand(what: string, run: () => Promise<string | null>) {
  return (event: Office.AddinCommands.Event): void => {
    run()
      .then((text) => text && notifyInPane(text, "info"))
      .catch((e) => {
        console.error(`EAV: ${what} failed`, e);
        notifyInPane(`${what}: ${e instanceof Error ? e.message : String(e)}`);
      })
      .then(() => event.completed());
  };
}

const saveCommand = (scope: FormulaScope) => valuesCommand("Save as Values", () => saveAsValues(valuesPort, scope, isEavFormula));

// As the add-in starts in a workbook: EAV formulas Excel calculated before the
// functions existed are stuck on #NAME?; enter them again, each once (repairNames.ts).
const repaired = new Set<string>();
Office.onReady(() => scheduleNameRepair(() => repairNameErrors(excelNameRepairPort, isEavFormula, repaired)));

// The ids are the manifest's <FunctionName>s.
Office.actions.associate("refreshSelection", refreshCommand("selection"));
Office.actions.associate("refreshSheet", refreshCommand("sheet"));
Office.actions.associate("refreshWorkbook", refreshCommand("workbook"));
Office.actions.associate("showSearch", paneCommand("search"));
Office.actions.associate("showAccount", paneCommand("account"));
Office.actions.associate("showSettings", paneCommand("settings"));
Office.actions.associate("insertChart", insertChartCommand);
Office.actions.associate("saveValuesSelection", saveCommand("selection"));
Office.actions.associate("saveValuesSheet", saveCommand("sheet"));
Office.actions.associate("saveValuesWorkbook", saveCommand("workbook"));
Office.actions.associate("restoreFunctions", valuesCommand("Restore All Functions", () => restoreFunctions(valuesPort)));

// The task pane's Refresh buttons (refreshPane.ts, another bundle) call this one.
const paneRefresh: RefreshFn = refresh;
globalThis.eavRefresh = paneRefresh;
